import { TranslationsEditor } from '../../components/TranslationsEditor'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { formatMoney, resolveCurrencyCode } from '@/lib/money'
import * as categoryService from '../../services/categoryService'
import * as productService from '../../services/productService'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Alert } from '../../components/ui/Alert'
import { SkeletonDetailFields, SkeletonListPage } from '../../components/ui/Skeleton'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'

const DEFAULT_LIMIT = 20

type ProductRow = {
  id: number
  name: string
  slug: string
  sku: string
  price: unknown
  currency?: unknown
  stockQuantity: number
  isActive: boolean
}

function rowFromItem(item: unknown): ProductRow | null {
  if (!item || typeof item !== 'object') return null
  const o = item as Record<string, unknown>
  const id = typeof o.id === 'number' ? o.id : Number(o.id)
  if (!Number.isFinite(id)) return null
  return {
    id,
    name: String(o.name ?? ''),
    slug: String(o.slug ?? ''),
    sku: String(o.sku ?? ''),
    price: o.price,
    currency: o.currency,
    stockQuantity: typeof o.stockQuantity === 'number' ? o.stockQuantity : Number(o.stockQuantity ?? 0),
    isActive: o.isActive !== false,
  }
}

export default function CategoryDetail() {
  const { idOrSlug } = useParams()
  const navigate = useNavigate()
  const ref = idOrSlug?.trim() ?? ''

  const [category, setCategory] = useState<categoryService.CategoryDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [products, setProducts] = useState<ProductRow[]>([])
  const [productsTotal, setProductsTotal] = useState(0)
  const [productsLoading, setProductsLoading] = useState(false)
  const [productsError, setProductsError] = useState<string | null>(null)

  const skipQResetRef = useRef(true)

  useEffect(() => {
    const t = window.setTimeout(() => setQ(searchInput.trim()), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    if (skipQResetRef.current) {
      skipQResetRef.current = false
      return
    }
    setPage(1)
  }, [q])

  const loadCategory = useCallback(async () => {
    if (!ref) return
    setLoading(true)
    setError(null)
    try {
      const data = await categoryService.fetchCategoryByRef(ref)
      setCategory(data)
    } catch (e) {
      setCategory(null)
      setError(getUserFacingErrorMessage(e, 'Failed to load category'))
    } finally {
      setLoading(false)
    }
  }, [ref])

  useEffect(() => {
    void loadCategory()
  }, [loadCategory])

  useEffect(() => {
    if (!category?.slug) return
    let cancelled = false
    setProductsLoading(true)
    setProductsError(null)
    productService
      .fetchProducts({
        page,
        limit,
        categorySlug: category.slug,
        ...(q ? { q } : {}),
      })
      .then((res) => {
        if (cancelled) return
        setProducts(res.items.map(rowFromItem).filter((r): r is ProductRow => r != null))
        setProductsTotal(res.total)
      })
      .catch((e) => {
        if (!cancelled) {
          setProducts([])
          setProductsTotal(0)
          setProductsError(getUserFacingErrorMessage(e, 'Failed to load products'))
        }
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [category?.slug, page, limit, q])

  const totalPages = Math.max(1, Math.ceil(productsTotal / limit) || 1)
  const isStoreCategory = category?.source === 'store' && category.id != null

  if (!ref) {
    return (
      <Card>
        <Alert variant="destructive" title="Invalid category">
          No category was specified.
        </Alert>
        <div className="mt-4">
          <Link to="/categories">
            <Button type="button" variant="secondary">
              Back to categories
            </Button>
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
    <Card accent>
      <CardHeader
        eyebrow="Catalog"
        title={loading ? 'Category' : (category?.name ?? 'Category')}
        description={
          loading
            ? 'Loading category details…'
            : category
              ? `Slug: ${category.slug} · ${category.productCount} product${category.productCount === 1 ? '' : 's'}`
              : undefined
        }
        action={
          <div className="flex flex-wrap gap-2">
            <Link to="/categories">
              <Button type="button" size="sm" variant="secondary">
                All categories
              </Button>
            </Link>
            {category ? (
              <Link to={`/products?categorySlug=${encodeURIComponent(category.slug)}`}>
                <Button type="button" size="sm">
                  Open in products
                </Button>
              </Link>
            ) : null}
            {isStoreCategory && category?.id != null ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => navigate('/categories', { state: { editCategoryId: category.id } })}
              >
                Edit category
              </Button>
            ) : null}
          </div>
        }
      />

      {error ? (
        <div className="mb-6">
          <Alert variant="destructive" title="Could not load category">
            {error}
          </Alert>
        </div>
      ) : null}

      {loading ? (
        <SkeletonDetailFields fields={6} />
      ) : category ? (
        <div className="mb-8 grid gap-6 lg:grid-cols-[minmax(0,12rem)_1fr]">
          <div>
            {category.image ? (
              <img
                src={category.image}
                alt=""
                className="aspect-square w-full max-w-[12rem] rounded-2xl border border-border object-cover shadow-sm"
              />
            ) : (
              <div className="flex aspect-square w-full max-w-[12rem] items-center justify-center rounded-2xl border border-dashed border-border bg-muted/30 text-sm text-muted-foreground">
                No image
              </div>
            )}
          </div>
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Name</dt>
              <dd className="mt-1 text-sm font-medium text-foreground">{category.name}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Slug</dt>
              <dd className="mt-1 font-mono text-sm text-foreground">{category.slug}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Source</dt>
              <dd className="mt-1 capitalize text-sm text-foreground">{category.source}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Products</dt>
              <dd className="mt-1 tabular-nums text-sm text-foreground">{category.productCount}</dd>
            </div>
            {category.source === 'store' ? (
              <>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {category.isActive === false ? 'Inactive' : 'Active'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Display order</dt>
                  <dd className="mt-1 tabular-nums text-sm text-foreground">{category.sortOrder ?? '—'}</dd>
                </div>
              </>
            ) : null}
          </dl>
        </div>
      ) : null}

      {category ? (
        <section className="space-y-4">
          <div className="flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-foreground">Products in this category</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Server-filtered by slug <span className="font-mono text-foreground">{category.slug}</span>.
              </p>
            </div>
            <div className="w-full min-w-[200px] sm:max-w-xs">
              <Input
                id="category-products-search"
                label="Search products"
                placeholder="Name…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          {productsError ? (
            <Alert variant="destructive" title="Could not load products">
              {productsError}
            </Alert>
          ) : null}

          {productsLoading ? (
            <SkeletonListPage toolbarFields={0} columns={6} rows={6} />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-muted/50 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3.5 sm:px-5">Product</th>
                      <th className="px-4 py-3.5 sm:px-5">SKU</th>
                      <th className="px-4 py-3.5 sm:px-5">Price</th>
                      <th className="px-4 py-3.5 sm:px-5">Stock</th>
                      <th className="px-4 py-3.5 sm:px-5">Status</th>
                      <th className="px-4 py-3.5 text-right sm:px-5">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-card">
                    {products.map((item) => (
                      <tr key={item.id} className="hover:bg-muted/40">
                        <td className="px-4 py-3.5 font-medium text-foreground sm:px-5">{item.name}</td>
                        <td className="px-4 py-3.5 font-mono text-xs text-foreground sm:px-5">{item.sku || '—'}</td>
                        <td className="px-4 py-3.5 tabular-nums text-foreground sm:px-5">
                          {formatMoney(Number(item.price), resolveCurrencyCode(item.currency))}
                        </td>
                        <td className="px-4 py-3.5 tabular-nums text-foreground sm:px-5">{item.stockQuantity}</td>
                        <td className="px-4 py-3.5 sm:px-5">
                          <span
                            className={`inline-flex rounded-lg px-2 py-0.5 text-xs font-semibold ${
                              item.isActive
                                ? 'bg-primary/15 text-primary ring-1 ring-primary/25'
                                : 'bg-muted text-muted-foreground ring-1 ring-border'
                            }`}
                          >
                            {item.isActive ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-right sm:px-5">
                          <Link
                            to={`/products/${item.id}`}
                            className="text-xs font-semibold text-primary hover:underline"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                    {products.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground sm:px-5">
                          {q ? 'No products match your search in this category.' : 'No products in this category yet.'}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              {productsTotal > 0 ? (
                <div className="flex flex-col gap-3 border-t border-border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <p className="text-xs text-muted-foreground">
                    Showing{' '}
                    <span className="font-medium text-foreground">
                      {products.length === 0 ? 0 : (page - 1) * limit + 1}
                    </span>
                    –
                    <span className="font-medium text-foreground">{(page - 1) * limit + products.length}</span> of{' '}
                    <span className="font-medium text-foreground">{productsTotal}</span>
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      className="!px-3 !py-1.5 text-xs"
                      disabled={page <= 1 || productsLoading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span className="text-xs font-medium text-foreground">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      className="!px-3 !py-1.5 text-xs"
                      disabled={page >= totalPages || productsLoading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </section>
      ) : null}
    </Card>
    {isStoreCategory && category?.id != null ? (
      <TranslationsEditor
        entity="category"
        entityId={category.id}
        reloadKey={category.name}
        fields={[{ key: 'name', label: 'Category name' }]}
      />
    ) : null}
    </div>
  )
}
